import { CartLine } from '@msdyn365-commerce/retail-proxy';
import { IRequestContext } from '@msdyn365-commerce/core-internal';

import { clearCookie, getCookie } from '../../../shared/functions/cookie';
import { CookieNames } from '../../../shared/services/cookie-service/cookie-service.const';
import { CookieService } from '../../../shared/services/cookie-service/cookie-service';

/**
 * Reduced representation of a cart line stored in the 'onlyCart' cookie.
 * Matches the shape written by the legacy header cart helper.
 */
export interface ICartSnapshotLine {
    id?: string;
    productId?: number;
    barcode?: string;
    description?: string;
    quantity?: number;
}

/**
 * Maps D365 cart lines to the reduced 'onlyCart' snapshot representation.
 */
export const mapCartLinesToSnapshot = (cartLines: CartLine[] | undefined): ICartSnapshotLine[] =>
    (cartLines ?? []).map(line => ({
        id: line.LineId,
        productId: line.ProductId,
        barcode: line.Barcode,
        description: line.Description,
        quantity: line.Quantity
    }));

/**
 * Safely parses the 'onlyCart' cookie. Returns undefined for missing,
 * malformed, or structurally invalid snapshots.
 */
export const parseCartSnapshotCookie = (): ICartSnapshotLine[] | undefined => {
    const rawCookie = getCookie(CookieNames.Cart);
    if (rawCookie === '') {
        return undefined;
    }
    try {
        const parsed: unknown = JSON.parse(rawCookie);
        return Array.isArray(parsed) ? (parsed as ICartSnapshotLine[]) : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Persists the cart snapshot in the 'onlyCart' cookie at path=/.
 */
export const writeCartSnapshotCookie = (snapshot: ICartSnapshotLine[], request: IRequestContext): void => {
    const expires = new CookieService(request).getCookieExpiration();
    clearCookie(CookieNames.Cart);
    document.cookie = `${CookieNames.Cart}=${JSON.stringify(snapshot)}; expires=${expires.toUTCString()}; path=/;`;
};

/**
 * Compares the previous snapshot with the current cart using the legacy
 * duplicate-aware matching by barcode and exact quantity, and returns the
 * current cart lines that have no counterpart in the previous snapshot.
 * An empty previous snapshot yields no unmatched lines, so a fresh baseline
 * never reports the whole cart as externally added.
 */
export const findUnmatchedCartLines = (
    previousSnapshot: ICartSnapshotLine[] | undefined,
    currentSnapshot: ICartSnapshotLine[]
): ICartSnapshotLine[] => {
    if (!previousSnapshot || previousSnapshot.length === 0) {
        return [];
    }

    const unmatchedLines: ICartSnapshotLine[] = [];
    const usedPreviousLineIds: string[] = [];

    currentSnapshot.forEach(line => {
        const previousLine = previousSnapshot.find(
            candidate =>
                candidate.barcode === line.barcode &&
                candidate.quantity === line.quantity &&
                !usedPreviousLineIds.includes(candidate.id ?? '')
        );
        if (previousLine) {
            usedPreviousLineIds.push(previousLine.id ?? '');
        } else {
            unmatchedLines.push(line);
        }
    });

    return unmatchedLines;
};
